import { notifyLearningFollowUpChanged } from "./LearningFollowUpSummary";
import EvidenceLearningQuestion from "./EvidenceLearningQuestion";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

function ResponseReview({ pupilId, item, onSaved }) {
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);
  const locked = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  if (!item.review_available) return null;
  const save = async event => {
    event.preventDefault();
    if (!decision || !note.trim() || locked.current) return;
    locked.current = true; setBusy(true); setError("");
    const sourceKind = item.kind === "understanding" ? "understanding" : "homework";
    const sourceId = sourceKind === "understanding" ? item.check_id : item.assignment_id;
    const endpoint = item.kind === "reflection"
      ? `/after-lesson-reflection/pupil/${encodeURIComponent(pupilId)}/lessons/${encodeURIComponent(item.lesson_id)}/review`
      : `/learner-framework/pupil/${encodeURIComponent(pupilId)}/learning-queue/${sourceKind}/${encodeURIComponent(sourceId)}/review`;
    try {
      const { data } = await axios.post(`${API}/api${endpoint}`, {
        source_revision: item.review_source_revision, expected_version: item.review_version,
        decision, note: note.trim(),
        ...(item.follow_up_response_revision ? { follow_up_response_revision: item.follow_up_response_revision } : {}),
      }, { withCredentials: true });
      if (data.ok !== true || data.review?.source_revision !== item.review_source_revision
          || data.review?.version !== item.review_version + 1 || data.review?.decision !== decision) {
        throw new Error("Review save not confirmed");
      }
      if (active.current) onSaved();
    } catch (err) {
      if (active.current) setError(err?.response?.status === 409
        ? "This response or review has changed. Your note is still here; reload the list before reviewing again."
        : "The review could not be confirmed as saved. Your note is still here; you can retry.");
    } finally {
      locked.current = false;
      if (active.current) setBusy(false);
    }
  };
  return <form onSubmit={save} className="mt-3 space-y-2 border-t pt-3">
    <p>Review this response only. Closing it does not confirm a driving skill or close a separate proof gap.</p>
    <button type="button" disabled={busy} className="block min-h-11 underline" aria-pressed={decision === "needs_follow_up"} onClick={() => setDecision("needs_follow_up")}>Keep open for follow-up</button>
    {decision === "needs_follow_up" && <p>This adds one question about this topic to the pupil's learning to-do list. Their answer will come back here for review.</p>}
    <button type="button" disabled={busy} className="block min-h-11 underline" aria-pressed={decision === "closed"} onClick={() => setDecision("closed")}>Close this response review</button>
    <label className="block">{decision === "closed" ? "Why is no further follow-up needed for this response?" : "What needs following up?"}
      <textarea value={note} onChange={event => setNote(event.target.value)} disabled={busy} maxLength={1000} rows={3} className="block w-full border p-2" />
    </label>
    <p>Your review note is part of the pupil's learning record.</p>
    {error && <p role="alert">{error}</p>}
    <button type="submit" disabled={busy || !decision || !note.trim()} className="min-h-11 underline">{busy ? "Saving review…" : "Save review"}</button>
  </form>;
}

function WithdrawQuestion({ pupilId, item, onSaved }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);
  const locked = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  if (!item.withdrawal_available) return null;
  const save = async event => {
    event.preventDefault();
    if (!reason.trim() || locked.current) return;
    locked.current = true; setBusy(true); setError("");
    const sourceKind = item.kind === "understanding" ? "understanding" : "homework";
    const sourceId = sourceKind === "understanding" ? item.check_id : item.assignment_id;
    const endpoint = item.kind === "reflection"
      ? `/after-lesson-reflection/pupil/${encodeURIComponent(pupilId)}/lessons/${encodeURIComponent(item.lesson_id)}/withdraw`
      : `/learner-framework/pupil/${encodeURIComponent(pupilId)}/learning-queue/${sourceKind}/${encodeURIComponent(sourceId)}/withdraw`;
    try {
      const { data } = await axios.post(`${API}/api${endpoint}`, {
        source_revision: item.review_source_revision, expected_version: item.review_version,
        follow_up_response_revision: item.follow_up_response_revision || null, reason: reason.trim(),
      }, { withCredentials: true });
      if (data.ok !== true || data.withdrawal?.source_revision !== item.review_source_revision
          || data.withdrawal?.expected_version !== item.review_version
          || data.withdrawal?.follow_up_response_revision !== (item.follow_up_response_revision || null)
          || data.withdrawal?.reason !== reason.trim() || !data.withdrawal?.withdrawn_at) {
        throw new Error("Withdrawal not confirmed");
      }
      if (active.current) onSaved();
    } catch (err) {
      if (active.current) setError(err?.response?.status === 409
        ? "The question or answer changed. Your reason is still here; reload before withdrawing."
        : "Withdrawal could not be confirmed. Your reason is still here; you can retry.");
    } finally {
      locked.current = false;
      if (active.current) setBusy(false);
    }
  };
  return <div className="mt-3 border-t pt-3">
    <button type="button" className="min-h-11 underline" aria-expanded={open} onClick={() => setOpen(!open)}>Withdraw question</button>
    {open && <form onSubmit={save} className="space-y-2">
      <p>Remove this question from active pupil work and current follow-up context. Its history and saved answers stay in the learning record. This does not record learning or close a practical proof gap.</p>
      <label className="block">Why is this question no longer needed?
        <textarea value={reason} onChange={event => setReason(event.target.value)} disabled={busy} maxLength={1000} rows={3} className="block w-full border p-2" />
      </label>
      {error && <p role="alert">{error}</p>}
      <button type="submit" className="min-h-11 underline" disabled={busy || !reason.trim()}>{busy ? "Withdrawing…" : "Save withdrawal"}</button>
    </form>}
  </div>;
}

export default function PupilUnderstandingQueue({ pupilId }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState("");
  const generation = useRef(0);
  const reads = useRef(0);
  const data = loaded?.pupilId === pupilId ? loaded.data : null;
  const load = useCallback(async version => {
    const sequence = ++reads.current;
    try {
      const response = await axios.get(`${API}/api/learner-framework/pupil/${encodeURIComponent(pupilId)}/learning-queue`, { withCredentials: true });
      if (generation.current === version && reads.current === sequence) { setLoaded({ pupilId, data: response.data }); setError(""); }
    } catch {
      if (generation.current === version && reads.current === sequence) setError("The pupil's learning to-do list could not load.");
    }
  }, [pupilId]);
  useEffect(() => {
    const version = ++generation.current;
    setLoaded(null); setError(""); setReceipt("");
    if (open) load(version);
    return () => { generation.current += 1; };
  }, [open, load]);
  const saved = () => {
    notifyLearningFollowUpChanged(pupilId);
    setReceipt("Your review is saved. Practical competence has not been changed.");
    setLoaded(null); load(generation.current);
  };
  return <section className="border-t border-slate-200 py-3" data-testid="instructor-understanding-queue">
    <button type="button" className="min-h-11 underline" aria-expanded={open} onClick={() => setOpen(!open)}>Pupil learning to-do list</button>
    {open && <div className="space-y-3 text-sm">
      <EvidenceLearningQuestion key={pupilId} pupilId={pupilId} onSaved={() => { notifyLearningFollowUpChanged(pupilId); setLoaded(null); load(generation.current); }} />
      {receipt && <p role="status">{receipt}</p>}
      {error && <p role="alert">{error}</p>}
      <button type="button" className="min-h-11 underline" onClick={() => load(generation.current)}>Reload learning list</button>
      {!data && !error && <p>Loading questions…</p>}
      {data && <>
        <p>{data.counts_complete === false ? "At least " : ""}{data.pending_count} awaiting pupil answers · {data.awaiting_review_count} awaiting your review</p>
        {!!data.withdrawn_count && <p>{data.withdrawn_count} questions withdrawn. No learning or practical competence is confirmed by withdrawal.</p>}
        {!!data.reviewed_count && <p>{data.reviewed_count} response reviews closed. This is not a skill-completion count.</p>}
        <p>Answers and AI assessments are learning evidence for review. They do not confirm practical competence.</p>
        {(data.items || []).map(item => <details key={item.id} className="border-t py-2">
          <summary className="min-h-11 cursor-pointer">{item.focus || "Understanding"} · {item.state === "withdrawn" ? "Question withdrawn" : item.state === "needs_answer" ? "Awaiting pupil" : item.state === "deferred" ? "Optional reflection left aside" : item.state === "reviewed" ? "Response review closed" : item.review?.decision === "needs_follow_up" ? "Follow-up needed" : "Awaiting review"}</summary>
          <p>{item.original_question || item.check_question}</p>
          <p>{item.reason}</p>
          {item.learner_outcome && <p className="mt-2">Pupil report: {({ understood: "I understand it", practised: "I tried it", need_more_help: "I need more help", not_confident: "I’m still unsure", completed: "Completed", deferred: "Not now — not confirmed learning" })[item.learner_outcome] || item.learner_outcome}</p>}
          {item.support_needed && <p>{item.state === "reviewed" ? "The pupil requested help in this response. A response review does not close any separate evidence gap." : "Help is still requested; this evidence gap remains open."}</p>}
          {item.kind === "reflection" && <p className="mt-2">Support requested: {item.support_requested_percent == null ? "not stated" : `${item.support_requested_percent}%`}. Support the pupil felt they received: {item.support_received_percent == null ? "not stated" : `${item.support_received_percent}%`}. These are separate from learning progress.</p>}
          {item.learner_answer && <p className="mt-2">Pupil answer: {item.learner_answer}</p>}
          {item.clarification_question && <p className="mt-2">Follow-up: {item.clarification_question}</p>}
          {item.clarification_answer && <p className="mt-2">Pupil clarification: {item.clarification_answer}</p>}
          {item.follow_up_question && <p className="mt-2">Requested follow-up: {item.follow_up_question}</p>}
          {item.follow_up_answer && <p className="mt-2">Pupil follow-up answer: {item.follow_up_answer}</p>}
          {item.state !== "withdrawn" && item.follow_up_question && !item.follow_up_answered_at && <p>This follow-up is awaiting the pupil's answer before review.</p>}
          <p className="mt-2">{item.evidence_required}</p>
          <p className="mt-2 text-slate-500">Source: {({ instructor_topic_question: "Instructor question without an attached record", instructor_evidence_question: "Instructor question about earlier evidence", homework_assignment: "Assigned homework", lesson_preparation: "Booked lesson preparation", after_lesson_reflection: "Pupil lesson reflection" })[item.source?.type] || "Karen understanding conversation"} · {item.source?.created_at || "Date unavailable"}</p>
          {item.source?.lesson_id && <p>Lesson reference: {item.source.lesson_id}</p>}
          {item.source?.evidence_id && <p>Evidence reference: {item.source.evidence_id}</p>}
          {item.review && <p className="mt-2">Instructor review: {item.review.note} · {item.review.reviewed_at}</p>}
          {item.withdrawal && <p className="mt-2">Withdrawn: {item.withdrawal.reason} · {item.withdrawal.withdrawn_at}. Question and answer history retained; practical evidence remains separate.</p>}
          <WithdrawQuestion key={`withdraw:${pupilId}:${item.id}:${item.review_source_revision}:${item.review_version}:${item.follow_up_response_revision}`} pupilId={pupilId} item={item} onSaved={() => {
            notifyLearningFollowUpChanged(pupilId);
            setReceipt("Question withdrawn. History is retained; learning and practical competence have not been changed.");
            setLoaded(null); load(generation.current);
          }} />
          <ResponseReview key={`${pupilId}:${item.id}:${item.review_source_revision}:${item.review_version}:${item.follow_up_response_revision}`} pupilId={pupilId} item={item} onSaved={saved} />
        </details>)}
        {data.has_more && <p>Showing up to 50 learning items. More source records may be available.</p>}
      </>}
    </div>}
  </section>;
}
